import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { siteConfig } from '@/siteConfig';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';

export const Industries = () => {
  const { language, t } = useLanguage();

  useEffect(() => {
    document.title = language === 'fr' ? 'Secteurs | NgSys Automatisation' : 'Industries | NgSys Automation';
  }, [language]);

  const useCases = {
    fr: [
      { industry: 'Manufacturier', use: 'Automatisation de lignes de production' },
      { industry: 'Agroalimentaire', use: 'Contrôle de procédés, traçabilité' },
      { industry: 'Emballage', use: 'Convoyeurs, vision, robotique' },
      { industry: 'Transformation plastique', use: 'Extrusion, injection, contrôle de température' },
      { industry: 'Machines spéciales', use: 'Contrôle motion, séquences complexes' },
      { industry: 'Bâtiments industriels', use: 'CVC, distribution électrique, alarmes' },
    ],
    en: [
      { industry: 'Manufacturing', use: 'Production line automation' },
      { industry: 'Food & Beverage', use: 'Process control, traceability' },
      { industry: 'Packaging', use: 'Conveyors, vision, robotics' },
      { industry: 'Plastics Processing', use: 'Extrusion, injection, temperature control' },
      { industry: 'Special Machines', use: 'Motion control, complex sequences' },
      { industry: 'Industrial Buildings', use: 'HVAC, electrical distribution, alarms' },
    ],
  };

  return (
    <>
      <div className="flex flex-col">
        {/* Hero */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/5">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-testid="industries-title">
              {language === 'fr' ? 'Secteurs Desservis' : 'Industries Served'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'fr'
                ? 'Nous servons une grande variété de secteurs industriels à travers le Canada avec des solutions sur mesure.'
                : 'We serve a wide variety of industrial sectors across Canada with tailored solutions.'}
            </p>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-20" data-testid="industries-grid-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {useCases[language].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  data-testid={`industry-card-${idx}`}
                >
                  <Card className="hover:border-primary/50 transition-colors duration-300 h-full">
                    <CardContent className="p-6">
                      <h3 className="font-heading text-xl font-semibold mb-3 text-primary">
                        {item.industry}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.use}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Differentiators */}
        <section className="py-20 md:py-32 bg-muted/30" data-testid="differentiators-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Ce qui nous distingue' : 'What Sets Us Apart'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {siteConfig.differentiators[language].map((diff, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: idx * 0.1 }}
                >
                  <Card>
                    <CardContent className="p-6 text-center">
                      <p className="text-sm font-medium">{diff}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};